import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizFreetext, {
  type QuizFreeTextState,
} from "@/components/storyboard/quiz/QuizFreetext";
import { useValidationSubmissions } from "@/hooks/useValidationSubmissions";
import { resolveFreetextStatus } from "@/lib/freetextQuizStatus";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizFreeTextEntryData } from "@/types/storyboard";
import type { ValidationResult } from "@/services/QuizValidationService";

export interface QuizFreetextFlowProps {
  id: string;
  title: string;
  data: QuizFreeTextEntryData;
  onValidate: (answer: string) => Promise<ValidationResult>;
}

/** Free-text quiz in the shared interactive-element shell. The answer and its
 * verdict live on the server, so a reload — or another device — meets the quiz
 * in the state the learner left it.
 */
const QuizFreetextFlow = ({
  id,
  title,
  data,
  onValidate,
}: QuizFreetextFlowProps) => {
  const { submissions, refresh, isReady } = useValidationSubmissions(id);
  const statusOverride = resolveFreetextStatus(submissions[0], data);
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizFreeTextState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizFreeTextState>
      id={id}
      title={title}
      illustrationKind="freetext"
      layout="inline"
      statusOverride={statusOverride}
      supportsInProgress
      reportProgress
      renderQuiz={({ initialPartial, onPartialChange, onComplete }) => {
        // The quiz seeds from useState on first mount: wait for the local
        // in-progress state or for validation_data to settle.
        if (initialPartial == null && !isReady) return null;

        return (
          <QuizFreetext
            data={data}
            onValidate={async (answer) => {
              const result = await onValidate(answer);
              refresh();
              return result;
            }}
            initialPartial={initialPartial ?? serverResume}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizFreetextFlow;
